/**
 * DemocratCharts — drawing state.
 *
 * Trend lines (2 points) and horizontal lines (1 price). Selectable + editable
 * via drag handles on the chart. Per-instrument keying so switching symbols
 * hides irrelevant drawings.
 *
 * Schema v2 adds:
 *   - selectedId (transient — not persisted)
 *   - lineWidth on drawings
 *   - actions: select, updatePoint, updatePrice, updateColor, updateLineWidth
 */

import { create } from "zustand";

export type DrawingTool = "trend" | "horizontal" | "fib_retracement" | "fib_extension" | "cycle" | "eraser" | null;

export interface DrawingPoint {
  ts: string;
  price: number;
}

export interface TrendLineDrawing {
  id: string;
  kind: "trend";
  chartKey: string;
  start: DrawingPoint;
  end: DrawingPoint;
  color: string;
  lineWidth: number;
}

export interface HorizontalLineDrawing {
  id: string;
  kind: "horizontal";
  chartKey: string;
  price: number;
  color: string;
  lineWidth: number;
}

export interface FibRetracementDrawing {
  id: string;
  kind: "fib_retracement";
  chartKey: string;
  start: DrawingPoint;
  end: DrawingPoint;
  color: string;       // accent color for the "0" and "1" anchor lines
  lineWidth: number;
}

export interface FibExtensionDrawing {
  id: string;
  kind: "fib_extension";
  chartKey: string;
  /** First point of the impulsive leg */
  p0: DrawingPoint;
  /** End of the impulsive leg */
  p1: DrawingPoint;
  /** Retracement anchor — where the move pulled back to before extending */
  p2: DrawingPoint;
  color: string;
  lineWidth: number;
}

export interface CycleLineDrawing {
  id: string;
  kind: "cycle";
  chartKey: string;
  /** Anchor candle for the first cycle line */
  anchor: DrawingPoint;
  /** Distance to the second line — defines cycle length */
  second: DrawingPoint;
  /** How many forward cycles to project. Default 8. */
  count: number;
  color: string;
  lineWidth: number;
}

export type Drawing =
  | TrendLineDrawing
  | HorizontalLineDrawing
  | FibRetracementDrawing
  | FibExtensionDrawing
  | CycleLineDrawing;

const LS_KEY = "livecharts:drawings:v3";

function loadDrawings(): Drawing[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as Drawing[];
  } catch {
    /* ignore */
  }
  return [];
}

function saveDrawings(list: Drawing[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function chartKeyFor(symbol: string, exchange: string, timeframe: string): string {
  return `${symbol}:${exchange}:${timeframe}`;
}

interface DrawingsState {
  drawings: Drawing[];
  activeTool: DrawingTool;
  pendingPoints: DrawingPoint[];
  selectedId: string | null;

  setTool: (tool: DrawingTool) => void;
  cancelPending: () => void;
  addPendingPoint: (point: DrawingPoint, chartKey: string) => void;
  removeDrawing: (id: string) => void;
  clearForChart: (chartKey: string) => void;

  // Selection + editing
  select: (id: string | null) => void;
  updateColor: (id: string, color: string) => void;
  updateLineWidth: (id: string, width: number) => void;
  updateTrendPoint: (id: string, which: "start" | "end", point: DrawingPoint) => void;
  updateHorizontalPrice: (id: string, price: number) => void;
}

export const useDrawingsStore = create<DrawingsState>((set, get) => ({
  drawings: loadDrawings(),
  activeTool: null,
  pendingPoints: [],
  selectedId: null,

  setTool: (tool) => set({ activeTool: tool, pendingPoints: [], selectedId: null }),

  cancelPending: () => set({ pendingPoints: [] }),

  addPendingPoint: (point, chartKey) => {
    const { activeTool, pendingPoints, drawings } = get();
    if (!activeTool) return;

    // 1-click tools
    if (activeTool === "horizontal") {
      const drawing: HorizontalLineDrawing = {
        id: makeId(), kind: "horizontal", chartKey,
        price: point.price, color: "#ffb74d", lineWidth: 2,
      };
      const next = [...drawings, drawing];
      saveDrawings(next);
      set({ drawings: next, pendingPoints: [], activeTool: null, selectedId: drawing.id });
      return;
    }

    // 2-click tools — collect first point, commit on second
    const needPoints =
      activeTool === "trend" ? 2 :
      activeTool === "fib_retracement" ? 2 :
      activeTool === "cycle" ? 2 :
      activeTool === "fib_extension" ? 3 :
      0;

    const points = [...pendingPoints, point];
    if (points.length < needPoints) {
      set({ pendingPoints: points });
      return;
    }

    let drawing: Drawing | null = null;
    switch (activeTool) {
      case "trend":
        drawing = {
          id: makeId(), kind: "trend", chartKey,
          start: points[0], end: points[1], color: "#ffb74d", lineWidth: 2,
        };
        break;
      case "fib_retracement":
        drawing = {
          id: makeId(), kind: "fib_retracement", chartKey,
          start: points[0], end: points[1], color: "#9575cd", lineWidth: 1,
        };
        break;
      case "cycle":
        drawing = {
          id: makeId(), kind: "cycle", chartKey,
          anchor: points[0], second: points[1], count: 8,
          color: "#4fc3f7", lineWidth: 1,
        };
        break;
      case "fib_extension":
        drawing = {
          id: makeId(), kind: "fib_extension", chartKey,
          p0: points[0], p1: points[1], p2: points[2],
          color: "#9575cd", lineWidth: 1,
        };
        break;
    }
    if (!drawing) return;
    const next = [...drawings, drawing];
    saveDrawings(next);
    set({ drawings: next, pendingPoints: [], activeTool: null, selectedId: drawing.id });
  },

  removeDrawing: (id) => {
    const next = get().drawings.filter((d) => d.id !== id);
    saveDrawings(next);
    set({ drawings: next, selectedId: get().selectedId === id ? null : get().selectedId });
  },

  clearForChart: (chartKey) => {
    const next = get().drawings.filter((d) => d.chartKey !== chartKey);
    saveDrawings(next);
    set({ drawings: next, selectedId: null });
  },

  select: (id) => set({ selectedId: id }),

  updateColor: (id, color) => {
    const next = get().drawings.map((d) => (d.id === id ? { ...d, color } : d));
    saveDrawings(next);
    set({ drawings: next });
  },

  updateLineWidth: (id, lineWidth) => {
    const next = get().drawings.map((d) => (d.id === id ? { ...d, lineWidth } : d));
    saveDrawings(next);
    set({ drawings: next });
  },

  updateTrendPoint: (id, which, point) => {
    const next = get().drawings.map((d) => {
      if (d.id !== id || d.kind !== "trend") return d;
      return { ...d, [which]: point };
    });
    saveDrawings(next);
    set({ drawings: next });
  },

  updateHorizontalPrice: (id, price) => {
    const next = get().drawings.map((d) =>
      d.id === id && d.kind === "horizontal" ? { ...d, price } : d
    );
    saveDrawings(next);
    set({ drawings: next });
  },
}));

function makeId(): string {
  return `dr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}
